import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../context/AppContext';
import { useCpa } from '../context/CpaContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { CpaConnection } from '../types/cpa';
import ExpenseImportPage from './ExpenseImportPage';
import editorStyles from './ExpenseEditorPage.module.css';
import styles from './SettingsPage.module.css';

const DOCS_INBOX_RE = /^[A-Za-z0-9._%+\-]+@rivh\.it$/i;

function normalizeDocsInbox(value: string): string {
  let text = String(value || '').trim();
  text = text.replace(/^mailto:/i, '');
  text = text.replace(/[\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '');
  const angled = text.match(/<([^>]+)>/);
  if (angled) text = angled[1];
  text = text.replace(/^['"`]+|['"`]+$/g, '').trim();
  return text;
}

const emptyDocsDraft = (email = ''): CpaConnection => ({
  type: 'rivhit_docs',
  enabled: true,
  inbox: '',
  bcc: false,
  bccEmail: email,
});

const SettingsPage: React.FC = () => {
  const { connections, rivhitDocsEnabled, save, reload } = useCpa();
  const { state } = useApp();
  const { user } = useAuth();
  const companyName = state.auth.tenant?.name || '';
  const { showError, showSuccess } = useToast();
  const [saving, setSaving] = useState(false);
  const [docsDraft, setDocsDraft] = useState<CpaConnection | null>(null);
  const [addConnection, setAddConnection] = useState('');
  const [docsInboxHint, setDocsInboxHint] = useState(false);
  const docsInboxRef = useRef<HTMLInputElement>(null);

  const persist = async (extra: Record<string, unknown> = {}) => {
    setSaving(true);
    const response = await save(extra);
    setSaving(false);
    if (!response.success) {
      showError(response.error || 'שמירת ההגדרות נכשלה');
      return false;
    }
    await reload();
    return true;
  };

  const setConnectionEnabled = async (enabled: boolean) => {
    const existing = connections.find((item) => item.type === 'rivhit_docs');
    if (!enabled) {
      if (!existing || !rivhitDocsEnabled) return;
      const rest = connections.filter((item) => item.type !== 'rivhit_docs');
      await persist({ connections: [...rest, { ...existing, enabled: false }] });
      setAddConnection('');
      return;
    }
    if (!existing?.inbox) return;
    const rest = connections.filter((item) => item.type !== 'rivhit_docs');
    await persist({ connections: [...rest, { ...existing, enabled: true }] });
  };

  const openDocsFloat = () => {
    const existing = connections.find((item) => item.type === 'rivhit_docs');
    setDocsInboxHint(false);
    setDocsDraft(existing ? { ...existing } : emptyDocsDraft(user?.email || ''));
    setAddConnection('');
  };

  const saveDocsFloat = async () => {
    if (!docsDraft) return;
    const inbox = normalizeDocsInbox(docsDraft.inbox);
    if (!DOCS_INBOX_RE.test(inbox)) {
      setDocsInboxHint(true);
      docsInboxRef.current?.focus();
      return;
    }
    const next: CpaConnection = {
      ...docsDraft,
      inbox,
      enabled: true,
      bccEmail: docsDraft.bcc ? docsDraft.bccEmail.trim() : docsDraft.bccEmail,
    };
    const rest = connections.filter((item) => item.type !== 'rivhit_docs');
    const ok = await persist({ connections: [...rest, next] });
    if (ok) {
      setDocsDraft(null);
      setDocsInboxHint(false);
      showSuccess('RivhitDocs נשמר');
    }
  };

  return (
    <div className={styles.page}>
      <h1>הגדרות</h1>
      <div className={styles.modules}>
        <section className={styles.card}>
          <h2>ייבוא</h2>
          <div className={styles.cardBody}>
            <ExpenseImportPage embedded />
          </div>
        </section>
        <section className={styles.card}>
          <h2>חיבור</h2>
          <div className={styles.connectionBar}>
            <input
              type="checkbox"
              className={styles.connectionToggle}
              checked={rivhitDocsEnabled}
              disabled={saving}
              aria-label="חיבור"
              onChange={(e) => {
                void setConnectionEnabled(e.target.checked);
              }}
            />
            {rivhitDocsEnabled ? (
              <p className={styles.status}>
                {companyName ? (
                  <>
                    <bdi>{companyName}</bdi>{' '}
                  </>
                ) : null}
                מחובר כרגע ל-
                <button type="button" className={styles.linkButton} onClick={openDocsFloat}>
                  <bdi>RivhitDocs</bdi>
                </button>
              </p>
            ) : (
              <div className={styles.connectField}>
                <span>להתחבר ל</span>
                <select
                  id="cpa-connection-add"
                  value={addConnection}
                  aria-label="להתחבר ל"
                  onChange={(e) => {
                    const value = e.target.value;
                    setAddConnection(value);
                    if (value === 'rivhit_docs') openDocsFloat();
                  }}
                >
                  <option value="">בחירת חיבור</option>
                  <option value="rivhit_docs">RivhitDocs</option>
                </select>
              </div>
            )}
          </div>
        </section>
      </div>

      {docsDraft
        ? createPortal(
            <div className={styles.floatBackdrop} onClick={(event) => event.target === event.currentTarget && setDocsDraft(null)}>
              <div className={styles.floatDialog} role="dialog" aria-labelledby="rivhit-docs-title">
                <h3 id="rivhit-docs-title">RivhitDocs</h3>
                <div className={styles.field}>
                  <label htmlFor="rivhit-docs-inbox">כתובת ייחודית</label>
                  <input
                    id="rivhit-docs-inbox"
                    ref={docsInboxRef}
                    dir="ltr"
                    value={docsDraft.inbox}
                    onChange={(e) => {
                      setDocsInboxHint(false);
                      setDocsDraft({ ...docsDraft, inbox: e.target.value });
                    }}
                    placeholder="015436199-pylonaqz@rivh.it"
                    aria-invalid={docsInboxHint}
                  />
                  {docsInboxHint ? (
                    <p className={styles.fieldHint}>יש למלא את הכתובת הייחודית של RivhitDocs (…@rivh.it)</p>
                  ) : null}
                </div>
                <label className={styles.connectionRow}>
                  <input
                    type="checkbox"
                    checked={docsDraft.bcc}
                    onChange={(e) => setDocsDraft({ ...docsDraft, bcc: e.target.checked })}
                  />
                  שלח לי עותק
                </label>
                {docsDraft.bcc ? (
                  <div className={styles.field}>
                    <label htmlFor="rivhit-docs-bcc">עותק אל</label>
                    <input
                      id="rivhit-docs-bcc"
                      type="email"
                      dir="ltr"
                      value={docsDraft.bccEmail}
                      onChange={(e) => setDocsDraft({ ...docsDraft, bccEmail: e.target.value })}
                    />
                  </div>
                ) : null}
                <div className={styles.actions}>
                  <button type="button" className={editorStyles.backButton} onClick={() => setDocsDraft(null)}>
                    ביטול
                  </button>
                  <button type="button" className={editorStyles.saveButton} onClick={saveDocsFloat} disabled={saving}>
                    שמירה
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default SettingsPage;
